import React from 'react';
import { Navigate } from 'react-router-dom';
import { checkAdminStatus } from '../../utils/authUtils';

/**
 * Client-side route guard — defense in depth alongside the backend's real enforcement
 * (@PreAuthorize for role/admin routes, FeatureAccessFilter for per-user feature blocks —
 * those are what actually protect the data; this just avoids flashing a page shell at
 * someone who then gets 403'd on every API call it makes).
 *
 * Usage:
 *   <ProtectedRoute require="admin"><UserManagement /></ProtectedRoute>
 *   <ProtectedRoute require="superAdmin"><Settings /></ProtectedRoute>
 *   <ProtectedRoute require="feature" featureKey="GATE_PASSES"><GatePassManagement /></ProtectedRoute>
 *
 * @param {'admin'|'superAdmin'|'feature'} require - minimum role required, or 'feature' to
 *   check the per-user blocklist (featureKey prop required in that case)
 * @param {string} [featureKey] - required when require="feature"
 */
const ProtectedRoute = ({ require = 'admin', featureKey, children }) => {
  const userDataRaw = localStorage.getItem('userData');
  const { isAdmin, isSuperAdmin } = checkAdminStatus(userDataRaw);

  let allowed;
  if (require === 'superAdmin') {
    allowed = isSuperAdmin;
  } else if (require === 'feature') {
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const blocked = userData?.blockedFeatures;
    allowed = !(Array.isArray(blocked) && featureKey && blocked.includes(featureKey));
  } else {
    allowed = isAdmin;
  }

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
